import { useEffect, useRef } from 'react';

import { useHouseholdChatContext } from '@/modules/household/chat/contexts/householdChat.context';
import { useHouseholdWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts/useHouseholdWorkflowTrackersContext';
import { WorkflowTrackerKind } from '@/types/schema';

import { type NewDiagramPrompt } from '../contexts/artifactStudio.context';

/**
 * Opens the editor chat and, for a new diagram, sends its first request.
 * When the backend creates the artifact for the new diagram, `onArtifactCreated`
 * receives the artifact ID.
 */
export function useNewDiagramChat({
  artifactID,
  newDiagram,
  onArtifactCreated,
}: {
  artifactID: string | null;
  newDiagram: NewDiagramPrompt | undefined;
  onArtifactCreated: (artifactID: string) => void;
}) {
  const { conversationId, openChat, sendMessage } = useHouseholdChatContext();
  const { trackers } = useHouseholdWorkflowTrackersContext();
  // Strict mode runs mount effects twice. Send the request one time only.
  const sent = useRef(false);

  useEffect(() => {
    openChat();
  }, [openChat]);

  useEffect(() => {
    if (!newDiagram || sent.current) return;
    sent.current = true;
    void sendMessage(newDiagram.message, undefined, {
      referenceImage: newDiagram.referenceImage,
      additionalInput: { withPromptTemplate: newDiagram.withPromptTemplate },
    });
  }, [newDiagram, sendMessage]);

  // When a diagram generation begins, the backend creates an empty artifact
  // and links it and the conversation to the generation tracker.
  const createdArtifactID =
    artifactID || !conversationId
      ? null
      : trackers.find(
          (tracker) =>
            tracker.kind === WorkflowTrackerKind.DiagramGeneration &&
            tracker.conversation?.id === conversationId &&
            tracker.diagramArtifact
        )?.diagramArtifact?.id;
  useEffect(() => {
    if (createdArtifactID) onArtifactCreated(createdArtifactID);
  }, [createdArtifactID, onArtifactCreated]);
}
